/* Push subscription from the browser side (05 §6).
 *
 * iOS only allows `Notification.requestPermission()` from a real user gesture,
 * and only in an INSTALLED PWA — Safari tabs cannot subscribe at all. So this
 * never auto-prompts: Settings asks, and this reports honestly why it cannot
 * when it cannot. A greyed toggle with a reason beats a dead one.
 */

import { api } from './api';

export type PushState =
  | { status: 'unsupported'; reason: string }
  | { status: 'not_installed' }
  | { status: 'denied' }
  | { status: 'off' }
  | { status: 'on' };

/** iOS grants the Push API to installed PWAs only. */
function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as unknown as { standalone?: boolean }).standalone === true;
}

export async function readPushState(): Promise<PushState> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return isStandalone()
      ? { status: 'unsupported', reason: 'This browser has no Push API.' }
      : { status: 'not_installed' };
  }
  if (Notification.permission === 'denied') return { status: 'denied' };

  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return sub ? { status: 'on' } : { status: 'off' };
}

/** base64url → Uint8Array, the form `applicationServerKey` insists on. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function enablePush(): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    if (Notification.permission !== 'granted') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        return { ok: false, error: 'Notifications were not allowed. iOS only asks once — after that it is in iOS Settings → Notifications.' };
      }
    }

    const { public_key: publicKey } = await api.pushPublicKey();
    const reg = await navigator.serviceWorker.ready;

    // Replace rather than reuse: a stale subscription created with a different
    // VAPID key silently fails to receive anything.
    const existing = await reg.pushManager.getSubscription();
    if (existing) await existing.unsubscribe();

    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });

    await api.registerPush(sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } });
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Could not turn notifications on.' };
  }
}

export async function disablePush(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) await sub.unsubscribe();
  } catch {
    // Even if the browser side fails, tell the server to stop sending.
  }
  await api.deletePush();
}
